import { useState, useEffect, useMemo } from 'react';
import {
  Scale, Plus, Trash2, RefreshCw, Play, Eye, AlertTriangle,
  CheckCircle, Clock, Target, TrendingUp, TrendingDown, Loader2, X, Save,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import FeatureGate from '../components/FeatureGate';
import { Spinner, InfoTip } from '../components/ui';
import {
  listRebalancerSchedules, saveRebalancerSchedule, deleteRebalancerSchedule,
  previewRebalancerPlan, runRebalancerNow, getRebalancerHistory,
  getExchangeConnections, getExchangeMarkets,
} from '../services/api';

const PIE_COLORS = ['#34d399', '#60a5fa', '#f59e0b', '#a78bfa', '#f472b6', '#fb923c', '#22d3ee', '#facc15'];

const FREQUENCIES = [
  { id: 'manual', label: 'Manual only', desc: 'Only run when you click "Rebalance now"' },
  { id: 'hourly', label: 'Hourly',       desc: 'Worker checks every hour' },
  { id: 'daily',  label: 'Daily',         desc: 'Once every 24 hours' },
  { id: 'weekly', label: 'Weekly',        desc: 'Once every 7 days' },
];

// Starter presets — sensible default schedules new users can click to pre-fill the form.
const PRESETS = [
  {
    id: 'conservative',
    label: 'Conservative core',
    desc: '50/30/20 BTC/ETH/USDC · 5% drift · daily — the standard balanced setup',
    apply: {
      driftThreshold: 5, minTradeUsd: 10, frequency: 'daily', baseQuote: 'USDC',
      targets: [
        { asset: 'BTC',  percent: 50 },
        { asset: 'ETH',  percent: 30 },
        { asset: 'USDC', percent: 20 },
      ],
    },
  },
  {
    id: 'defensive',
    label: '60/40 defensive',
    desc: '60% BTC / 40% stable · 7% drift · weekly — minimum management overhead',
    apply: {
      driftThreshold: 7, minTradeUsd: 20, frequency: 'weekly', baseQuote: 'USDC',
      targets: [
        { asset: 'BTC',  percent: 60 },
        { asset: 'USDC', percent: 40 },
      ],
    },
  },
  {
    id: 'l1diverse',
    label: 'L1 diversified',
    desc: '30 BTC / 30 ETH / 20 SOL / 20 USDC · 5% drift · daily — moderate altcoin exposure',
    apply: {
      driftThreshold: 5, minTradeUsd: 10, frequency: 'daily', baseQuote: 'USDC',
      targets: [
        { asset: 'BTC',  percent: 30 },
        { asset: 'ETH',  percent: 30 },
        { asset: 'SOL',  percent: 20 },
        { asset: 'USDC', percent: 20 },
      ],
    },
  },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}

// ─── Schedule editor (create / update) ───────────────────────────────────────

// Sensible defaults shown until real markets load (or if the fetch fails).
const FALLBACK_QUOTES = ['USDT', 'USDC', 'USD'];

function ScheduleEditor({ schedule, exchanges, onSubmit, onCancel, saving }) {
  const isEdit = !!schedule;
  const [name, setName] = useState(schedule?.name || '');
  const [exchangeId, setExchangeId] = useState(schedule?.exchangeId || (exchanges[0]?.exchangeId || 'binance'));
  const [baseQuote, setBaseQuote] = useState(schedule?.baseQuote || 'USDT');
  const [driftThreshold, setDriftThreshold] = useState(schedule?.driftThreshold ?? 5);
  const [minTradeUsd, setMinTradeUsd] = useState(schedule?.minTradeUsd ?? 10);
  const [frequency, setFrequency] = useState(schedule?.frequency || 'manual');
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  const [targets, setTargets] = useState(
    schedule?.targets?.length
      ? [...schedule.targets]
      : [{ asset: 'BTC', percent: 50 }, { asset: 'ETH', percent: 30 }, { asset: 'USDT', percent: 20 }]
  );

  // Quote currencies are dynamic — different exchanges support different quote
  // assets (e.g. Binance US has no BUSD, Kraken has EUR/GBP, Coinbase has GUSD).
  // We fetch the exchange's real markets and rank distinct quotes by how many
  // pairs use them, so the most-traded quote bubbles to the top.
  const [quoteOptions, setQuoteOptions] = useState(FALLBACK_QUOTES);
  const [quotesLoading, setQuotesLoading] = useState(false);

  useEffect(() => {
    if (!exchangeId) return;
    let cancelled = false;
    setQuotesLoading(true);
    getExchangeMarkets(exchangeId)
      .then(res => {
        if (cancelled) return;
        const markets = res?.markets || [];
        if (markets.length === 0) { setQuoteOptions(FALLBACK_QUOTES); return; }
        // Count each quote's usage; sort desc; cap at 12 so the dropdown stays usable.
        const counts = new Map();
        for (const m of markets) {
          if (!m.quote) continue;
          counts.set(m.quote, (counts.get(m.quote) || 0) + 1);
        }
        const sorted = [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([q]) => q)
          .slice(0, 12);
        const options = sorted.length ? sorted : FALLBACK_QUOTES;
        setQuoteOptions(options);
        // If the saved baseQuote no longer exists on this exchange, snap to the
        // most-used quote so the rebalancer can actually price its assets.
        if (!options.includes(baseQuote)) setBaseQuote(options[0]);
      })
      .catch(() => { if (!cancelled) setQuoteOptions(FALLBACK_QUOTES); })
      .finally(() => { if (!cancelled) setQuotesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeId]);

  const sum = useMemo(() => targets.reduce((a, t) => a + (Number(t.percent) || 0), 0), [targets]);
  const sumValid = Math.abs(sum - 100) < 0.5;

  function update(i, key, value) {
    setTargets(t => t.map((x, idx) => idx === i ? { ...x, [key]: key === 'percent' ? Number(value) : String(value).toUpperCase() } : x));
  }
  function addRow() {
    if (targets.length >= 12) return;
    setTargets(t => [...t, { asset: '', percent: 0 }]);
  }
  function removeRow(i) {
    setTargets(t => t.filter((_, idx) => idx !== i));
  }
  function balance() {
    const equal = +(100 / targets.length).toFixed(2);
    setTargets(t => t.map(x => ({ ...x, percent: equal })));
  }

  function submit() {
    if (!name.trim()) return;
    if (!sumValid) return;
    if (!exchangeId) return;
    onSubmit({
      scheduleId: schedule?.scheduleId,
      name: name.trim(),
      exchangeId,
      baseQuote: baseQuote.toUpperCase(),
      driftThreshold: Number(driftThreshold),
      minTradeUsd: Number(minTradeUsd),
      frequency,
      enabled,
      targets: targets.map(t => ({ asset: String(t.asset || '').toUpperCase().trim(), percent: Number(t.percent) })),
    });
  }

  function applyPreset(preset) {
    setDriftThreshold(preset.apply.driftThreshold);
    setMinTradeUsd(preset.apply.minTradeUsd);
    setFrequency(preset.apply.frequency);
    setBaseQuote(preset.apply.baseQuote);
    setTargets(preset.apply.targets.map(t => ({ ...t })));
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{isEdit ? 'Edit schedule' : 'New schedule'}</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {/* Starter presets — visible on new schedules only, hidden when editing */}
      {!isEdit && (
        <div className="mb-4 bg-dark-700/30 border border-dark-600/40 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Start from a preset</span>
            <InfoTip side="right">One-click fills the form with a sensible default. You can edit any field after. Saves you from guessing on first run.</InfoTip>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {PRESETS.map(p => (
              <button key={p.id} type="button" onClick={() => applyPreset(p)}
                className="text-left bg-dark-800 hover:bg-dark-700 border border-dark-600 hover:border-accent-primary/40 rounded-lg p-2.5 transition-all">
                <div className="text-xs font-semibold text-white">{p.label}</div>
                <div className="text-[10px] text-gray-500 leading-snug mt-0.5">{p.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Schedule name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Core portfolio" maxLength={64}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
          <p className="text-[10px] text-gray-600 mt-1">Anything memorable — "Core portfolio", "Retirement stack", etc.</p>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Exchange</label>
          <select value={exchangeId} onChange={e => setExchangeId(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {(exchanges?.length ? exchanges : [{ exchangeId: 'binance', name: 'Binance' }]).map(ex => (
              <option key={ex.exchangeId} value={ex.exchangeId}>
                {ex.name || ex.exchangeId}{ex.connected === false ? ' (not connected)' : ''}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-gray-600 mt-1">Where the rebalance trades will actually execute.</p>
        </div>
        <div>
          <label className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
            <span className="flex items-center gap-1.5">
              Quote currency
              <InfoTip side="right">The currency every rebalance trade is priced in. USDT is the safest default on most exchanges; USDC works equally well. Dynamic per-exchange — Coinbase shows USD/USDC/EUR, Kraken adds EUR/GBP, Binance shows USDT/USDC/FDUSD/etc.</InfoTip>
            </span>
            <span className="text-gray-600 text-[10px]">
              {quotesLoading ? 'Loading…' : `${quoteOptions.length} on this exchange`}
            </span>
          </label>
          <select value={baseQuote} onChange={e => setBaseQuote(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {quoteOptions.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          <p className="text-[10px] text-gray-600 mt-1">The currency every trade is priced in (e.g. BTC/USDT means USDT is the quote).</p>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Drift threshold
            <InfoTip side="right">Only rebalance when an asset is at least this far from its target. 5% is the sensible default for crypto majors. Lower = tighter tracking + more fees. Higher = less frequent + more tax-friendly. Use 2-3% for high-volume venues, 10%+ for volatile alts.</InfoTip>
          </label>
          <div className="flex items-center gap-2">
            <input type="number" value={driftThreshold}
              onChange={e => setDriftThreshold(e.target.value)} min={0.1} max={50} step={0.5}
              className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
            <span className="text-gray-500 text-sm">%</span>
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Only rebalance when an asset is at least this far from its target. 5% is a sensible default.</p>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Min trade size
            <InfoTip side="right">Skip rebalance trades smaller than this USD amount. Default $10 stops the worker from generating dust trades that lose to fees. Set higher ($50+) on high-fee venues; lower ($5) on Coinbase Advanced or Binance VIP tiers.</InfoTip>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">$</span>
            <input type="number" value={minTradeUsd}
              onChange={e => setMinTradeUsd(e.target.value)} min={1} step={1}
              className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none" />
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Skip trades smaller than this — keeps fees from eating tiny rebalances.</p>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            Frequency
            <InfoTip side="right">Manual = safest while learning (use Preview + Run Now). Daily = sweet spot for most users. Weekly = academic standard for buy-and-hold + tax-friendly. Hourly = only for zero-fee tiers + tight tracking.</InfoTip>
          </label>
          <select value={frequency} onChange={e => setFrequency(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label} — {f.desc}</option>)}
          </select>
          <p className="text-[10px] text-gray-600 mt-1">"Manual only" = safest while you're learning. Switch to hourly/daily after you trust the previews.</p>
        </div>
      </div>

      <div className="border-t border-dark-700/50 pt-4 mb-3">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs uppercase tracking-wider text-gray-400 font-bold">Target allocation</h4>
          <div className="flex items-center gap-2">
            <button onClick={balance} className="text-xs text-accent-primary hover:text-accent-primary/80">Equal split</button>
            <button onClick={addRow} className="text-xs text-accent-primary hover:text-accent-primary/80 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add asset
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {targets.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={t.asset} onChange={e => update(i, 'asset', e.target.value)}
                placeholder="BTC" maxLength={12}
                className="w-24 bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-white text-sm font-mono focus:border-accent-primary focus:outline-none" />
              <input type="number" value={t.percent} onChange={e => update(i, 'percent', e.target.value)}
                min={0} max={100} step={0.5}
                className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
              <span className="text-gray-500 text-xs w-6">%</span>
              <button onClick={() => removeRow(i)} disabled={targets.length <= 2}
                className="text-gray-600 hover:text-red-400 disabled:opacity-30">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className={`mt-3 text-xs flex items-center gap-2 ${sumValid ? 'text-emerald-400' : 'text-red-400'}`}>
          {sumValid ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          Sum: {sum.toFixed(2)}% {sumValid ? '— ready to save' : '— must total 100%'}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input type="checkbox" id="rebal-enabled" checked={enabled} onChange={e => setEnabled(e.target.checked)}
          className="accent-accent-primary" />
        <label htmlFor="rebal-enabled" className="text-xs text-gray-400">Schedule enabled (worker may execute it on its frequency)</label>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm text-gray-400 border border-dark-600 hover:text-white transition-all">
          Cancel
        </button>
        <button onClick={submit} disabled={!sumValid || !name.trim() || saving}
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? 'Update schedule' : 'Create schedule'}
        </button>
      </div>
    </Card>
  );
}

// ─── Plan preview / run summary ──────────────────────────────────────────────

function PlanPanel({ plan, onClose, onConfirm, executing }) {
  const isInBalance = plan?.status === 'in_balance';
  const isUnpriced  = plan?.status === 'unpriced';
  const needsRebal  = plan?.status === 'rebalance_needed';

  const allocationData = (plan?.allocation?.breakdown || []).map((b, i) => ({
    name: b.asset,
    value: Math.max(0, b.currentUsd),
    targetPct: b.targetPct,
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Rebalance preview</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {isInBalance && (
        <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-300">Portfolio is in balance</p>
            <p className="text-xs text-emerald-300/80 mt-0.5">{plan.reason}</p>
          </div>
        </div>
      )}

      {isUnpriced && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Cannot price every asset</p>
            <p className="text-xs text-amber-300/80 mt-0.5">{plan.reason}</p>
          </div>
        </div>
      )}

      {plan?.allocation && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-4">
          <div>
            <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Current allocation</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={allocationData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={36}>
                    {allocationData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, _, e) => [`$${(v||0).toFixed(2)} — target ${e?.payload?.targetPct ?? '?'}%`, e?.payload?.name]}
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 12, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-gray-500 text-center mt-1">
              In-scope: ${plan.allocation.inScopeUsd?.toFixed(2)} · Out-of-scope: ${plan.allocation.outOfScopeUsd?.toFixed(2)}
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Per-asset drift</h4>
            <div className="space-y-2">
              {plan.allocation.breakdown.map(b => (
                <div key={b.asset} className="bg-dark-700/40 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white font-mono">{b.asset}</span>
                    <span className={`text-xs font-bold ${Math.abs(b.drift) > (plan.driftThreshold || 5) ? 'text-amber-400' : 'text-gray-500'}`}>
                      {b.drift > 0 ? '+' : ''}{b.drift}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Now {b.currentPct}% (${b.currentUsd?.toFixed(2)}) — Target {b.targetPct}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {needsRebal && plan.trades.length > 0 && (
        <div className="border-t border-dark-700/50 pt-4">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Trades the rebalancer will place</h4>
          <div className="space-y-2 mb-4">
            {plan.trades.map((t, i) => (
              <div key={i} className="flex items-center gap-3 bg-dark-700/40 rounded-lg px-3 py-2">
                {t.side === 'sell'
                  ? <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                  : <TrendingUp className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                <span className={`text-xs font-bold uppercase w-12 ${t.side === 'sell' ? 'text-red-400' : 'text-emerald-400'}`}>{t.side}</span>
                <span className="text-sm text-white font-mono flex-1">{t.symbol}</span>
                <span className="text-sm text-white font-bold">${t.amountUsd.toFixed(2)}</span>
              </div>
            ))}
          </div>
          {onConfirm && (
            <button onClick={onConfirm} disabled={executing}
              className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
              {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {executing ? 'Executing rebalance…' : 'Execute rebalance now'}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Schedule list row ───────────────────────────────────────────────────────

function ScheduleRow({ schedule, onPreview, onRun, onEdit, onDelete, busy }) {
  const status = schedule.lastRunStatus;
  const lastRun = schedule.lastRunAt ? new Date(schedule.lastRunAt).toLocaleString() : 'never';
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-white font-semibold truncate">{schedule.name}</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${schedule.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-400'}`}>
              {schedule.enabled ? 'ENABLED' : 'PAUSED'}
            </span>
            <span className="text-[10px] text-gray-500 font-mono uppercase">{schedule.frequency}</span>
          </div>
          <p className="text-xs text-gray-500">{schedule.exchangeId} · {schedule.targets.length} assets · drift {schedule.driftThreshold}% · min ${schedule.minTradeUsd}</p>
          <p className="text-[10px] text-gray-600 mt-1">Last run: {lastRun}{status ? ` — ${status}` : ''}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onPreview(schedule)} disabled={busy === schedule.scheduleId}
            title="Preview rebalance plan"
            className="p-2 rounded-lg text-gray-400 hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-40">
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onRun(schedule)} disabled={busy === schedule.scheduleId}
            title="Rebalance now"
            className="p-2 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all disabled:opacity-40">
            {busy === schedule.scheduleId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onEdit(schedule)} title="Edit"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700 transition-all">
            <Target className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(schedule)} title="Delete"
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {schedule.targets.map((t, i) => (
          <span key={i} className="text-[10px] bg-dark-700 text-gray-400 px-2 py-0.5 rounded font-mono">
            {t.asset} {t.percent}%
          </span>
        ))}
      </div>
    </Card>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function RebalancerPage() {
  const [schedules, setSchedules] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [planFor, setPlanFor] = useState(null);   // { schedule, plan }
  const [executing, setExecuting] = useState(false);
  const [toast, setToast] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [list, conn] = await Promise.all([
        listRebalancerSchedules().catch(() => ({ schedules: [] })),
        getExchangeConnections().catch(() => ({ exchanges: [] })),
      ]);
      setSchedules(list?.schedules || []);
      setExchanges((conn?.exchanges || []).filter(e => e.connected));
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Could not load schedules' });
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleSave(payload) {
    setSaving(true);
    try {
      await saveRebalancerSchedule(payload);
      setToast({ type: 'success', message: payload.scheduleId ? 'Schedule updated' : 'Schedule created' });
      setShowNew(false);
      setEditing(null);
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Save failed' });
    }
    setSaving(false);
  }

  async function handleDelete(schedule) {
    if (!window.confirm(`Delete "${schedule.name}"? Its history is kept on disk.`)) return;
    try {
      await deleteRebalancerSchedule(schedule.scheduleId);
      setToast({ type: 'success', message: 'Schedule deleted' });
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  }

  async function handlePreview(schedule) {
    setBusyId(schedule.scheduleId);
    try {
      const res = await previewRebalancerPlan(schedule.scheduleId);
      setPlanFor({ schedule, plan: { ...res.plan, driftThreshold: schedule.driftThreshold }, totalUsd: res.totalUsd });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Preview failed' });
    }
    setBusyId(null);
  }

  async function handleRun(schedule) {
    if (!window.confirm(`Rebalance "${schedule.name}" now? Real orders will be placed on ${schedule.exchangeId}.`)) return;
    setBusyId(schedule.scheduleId);
    try {
      const summary = await runRebalancerNow(schedule.scheduleId);
      const filled = (summary.executed || []).filter(t => t.status === 'filled').length;
      const total = (summary.executed || []).length;
      setToast({
        type: summary.status === 'ok' ? 'success' : summary.status === 'partial' ? 'warning' : 'error',
        message: `Rebalance ${summary.status}: ${filled}/${total} trades filled${summary.reason ? ` — ${summary.reason}` : ''}`,
      });
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Run failed' });
    }
    setBusyId(null);
  }

  async function handleConfirmFromPreview() {
    if (!planFor?.schedule) return;
    setExecuting(true);
    try {
      const summary = await runRebalancerNow(planFor.schedule.scheduleId);
      const filled = (summary.executed || []).filter(t => t.status === 'filled').length;
      const total = (summary.executed || []).length;
      setToast({
        type: summary.status === 'ok' ? 'success' : summary.status === 'partial' ? 'warning' : 'error',
        message: `Rebalance ${summary.status}: ${filled}/${total} trades filled`,
      });
      setPlanFor(null);
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Run failed' });
    }
    setExecuting(false);
  }

  return (
    <FeatureGate feature="portfolioRebalancing">
      <div className="space-y-6 animate-fade-in">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
              <Scale className="w-6 h-6" /> Portfolio Rebalancer
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Set target allocations across your CEX holdings. The rebalancer fires real CCXT trades to bring your portfolio back to target when drift exceeds your threshold.
            </p>
          </div>
          <button onClick={() => { setEditing(null); setShowNew(true); }}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-all flex items-center gap-2">
            <Plus className="w-4 h-4" /> New schedule
          </button>
        </div>

        {/* Connected exchanges hint */}
        {!loading && exchanges.length === 0 && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-300">No connected exchanges</p>
              <p className="text-xs text-amber-300/80 mt-0.5">Connect a CEX in Settings → Connections before creating a rebalance schedule.</p>
            </div>
          </div>
        )}

        {/* Editor */}
        {(showNew || editing) && (
          <ScheduleEditor
            schedule={editing}
            exchanges={exchanges}
            saving={saving}
            onCancel={() => { setShowNew(false); setEditing(null); }}
            onSubmit={handleSave}
          />
        )}

        {/* Plan panel */}
        {planFor && (
          <PlanPanel
            plan={planFor.plan}
            executing={executing}
            onClose={() => setPlanFor(null)}
            onConfirm={planFor.plan?.status === 'rebalance_needed' ? handleConfirmFromPreview : null}
          />
        )}

        {/* Schedule list */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>
        ) : schedules.length === 0 ? (
          <Card className="text-center py-16">
            <Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No rebalance schedules yet.</p>
            <p className="text-gray-600 text-xs mt-1">Create one to keep your portfolio aligned to target allocations automatically.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {schedules.map(s => (
              <ScheduleRow
                key={s.scheduleId}
                schedule={s}
                busy={busyId}
                onPreview={handlePreview}
                onRun={handleRun}
                onEdit={(sched) => { setShowNew(false); setEditing(sched); }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Refresh */}
        <div className="flex justify-end">
          <button onClick={load} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}
